// FLAG:useDocuAi — the ONE dispatch predicate for the hardened upload path.
//
// Until now the hardening (bounded retry, request timeouts, `minioId` recorded on the row) existed
// only for the receipt uploaders, source 'expense' | 'revenue'. A Documents/inbox drop runs
// with source 'documents' and therefore got exactly one attempt per step, no timeout, and no
// recorded minioId — a mass upload that lost a connection mid-batch failed terminally on the first
// try, and the flyout's retry button then re-POSTED the whole file (creating a second DMS version)
// because the row carried nothing to tell it the bytes had already landed.
//
// Documents are opted in HERE and nowhere else, so the footprint is one grep away and retirement is
// a one-line deletion. With useDocuAi OFF `isHardenedUploadSource` collapses to
// `isReceiptUploadSource` — the exact predicate every call site used before — and receipt sources
// cannot exist flag-OFF at all (their call sites gate on isDmsReceiptUploadEnabled). So flag-OFF is
// the untouched baseline by construction, not by audit.

import { user } from 'ampersand-app'
import { experimentalFeatures } from '@/modules/base/util/constants'
import { isReceiptUploadSource } from '@/redux/ducks/document/_helpers/uploadAdmission'

// The source used by the Documents list, folder view, and drag-and-drop zone.
export const DOCUMENT_UPLOAD_SOURCE = 'documents'

/**
 * Whether this user gets the hardened upload behaviour at all.
 *
 * Read off the JWT-derived feature list like every other client-side flag check, so a tenant that
 * has not been switched over keeps the pre-existing path.
 *
 * Deliberately useDocuAi ALONE, unlike isDmsReceiptUploadEnabled which also demands
 * dmsDirectUpload. The receipt uploaders need that second check because they choose between two
 * different upload endpoints. Here the check happens INSIDE the DMS direct-upload chain
 * (prepare-upload → upload-with-token → complete-upload), which a tenant without dmsDirectUpload
 * never enters: createDmsUploader hands those tenants the default uploader and
 * fetchPendingUploads returns early. Adding the second flag would be a condition that cannot be
 * false at this point.
 */
export const isUploadHardeningEnabled = (): boolean =>
  Boolean(user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi))

/**
 * Whether a queued upload runs the hardened chain (retry + timeouts + recorded minioId).
 *
 * Deliberately NOT reused for the SCHEDULER's admission rule: `isReceiptUploadSource` also selects
 * the in-flight-set admission in uploadAdmission/processUploads, and moving documents onto that
 * rule would change how many files run at once — a different change with a different risk profile.
 * Admission stays exactly as it is; only the per-file chain is hardened.
 */
export const isHardenedUploadSource = (source?: string): boolean =>
  isReceiptUploadSource(source)
  || (source === DOCUMENT_UPLOAD_SOURCE && isUploadHardeningEnabled())
